import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, Typography, Paper, Grid, Card, CardContent, Button, 
  TextField, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Stack, Alert, CircularProgress, Divider, Tooltip, Chip, MenuItem, Select,
  FormControl, InputLabel, InputAdornment, List, ListItem, ListItemText,
  ListItemIcon, ListItemSecondaryAction, Tab, Tabs, Accordion, AccordionSummary,
  AccordionDetails, Fade, Grow, LinearProgress, Switch, Slider, FormControlLabel,
  Checkbox
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useKeycloak } from '@react-keycloak/web';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ingestFile, getFiles, deleteFile, crawlWebsite, getUploadStatus,
  setApiKey, setApiEndpoint
} from '../../api/services/agent-studio/AgentFeatures';
import { 
  CloudUpload as CloudUploadIcon, Description as DescriptionIcon, 
  Delete as DeleteIcon, Visibility as VisibilityIcon, 
  Language as LanguageIcon, Check as CheckIcon, 
  Warning as WarningIcon, Error as ErrorIcon, 
  Refresh as RefreshIcon, FileCopy as FileCopyIcon, 
  Launch as LaunchIcon, ExpandMore as ExpandMoreIcon,
  CloudQueue as CloudQueueIcon, ArrowBack as ArrowBackIcon,
  Close as CloseIcon
} from '@mui/icons-material';

const RAGUpload = ({ agentId, apiKey, endpoint }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [crawlDialogOpen, setCrawlDialogOpen] = useState(false);
  const [crawling, setCrawling] = useState(false);
  const [crawlUrlInput, setCrawlUrlInput] = useState('');
  const [maxPage, setMaxPage] = useState(100);
  const [maxDepth, setMaxDepth] = useState(3);
  const [crawlSubDomain, setCrawlSubDomain] = useState(true);
  const [crawlStatus, setCrawlStatus] = useState(null);
  const [statusInterval, setStatusInterval] = useState(null);
  const { serverId } = useParams();
  const { keycloak } = useKeycloak();
  const [loading, setLoading] = useState(false);

  const isValidUrl = crawlUrlInput.trim() === '' || crawlUrlInput.startsWith('https://');

  useEffect(() => {
    if (apiKey) {
      setApiKey(apiKey);
      if (endpoint) {
        setApiEndpoint(endpoint);
      }
      fetchDocuments();
    } else {
      setError('No API key available for this agent');
      setLoading(false);
    }
  }, [apiKey, endpoint]);

  useEffect(() => {
    return () => {
      if (statusInterval) {
        clearInterval(statusInterval);
      }
    };
  }, [statusInterval]);

  const fetchDocuments = async () => {
    try {
      if (!apiKey) {
        throw new Error('API key is missing');
      }

      setLoading(true);
      let allFiles = [];
      let currentPage = 1;
      let hasMorePages = true;

      while (hasMorePages) {
        const response = await getFiles(apiKey, currentPage, endpoint);
        const files = response.data.files;
        
        if (!files || files.length === 0) {
          hasMorePages = false;
        } else {
          allFiles = [...allFiles, ...files];
          currentPage++;
        }
      }

      setDocuments(allFiles.map(file => ({
        id: file.id,
        name: file.name,
        size: file.size_bytes,
        type: file.name.split('.').pop().toLowerCase(),
        date: new Date(file.created_at).toISOString().split('T')[0]
      })));
    } catch (err) {
      setError('Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setError(null);
    setSuccess(false);

    try {
      if (!apiKey) {
        throw new Error('API key is missing');
      }

      const validTypes = [
        '.pdf', '.txt', '.doc', '.docx', '.xlsx', '.pptx', '.md', '.zip',
        '.adoc', '.asciidoc', '.html', '.xhtml', '.htm', '.csv',
        '.png', '.jpg', '.jpeg', '.tiff', '.tif', '.bmp', '.webp'
      ];
      const maxSize = 50 * 1024 * 1024;

      const invalidFiles = files.filter(file => {
        const extension = '.' + file.name.split('.').pop().toLowerCase();
        return !validTypes.includes(extension) || file.size > maxSize;
      });

      if (invalidFiles.length > 0) {
        throw new Error('Some files are invalid. Please check file types and sizes (max 50MB).');
      }

      await ingestFile(files, apiKey, endpoint);

      const response = await getFiles(apiKey, 1, endpoint);
      setDocuments(response.data.files.map(file => ({
        id: file.id,
        name: file.name,
        size: file.size_bytes,
        type: file.name.split('.').pop().toLowerCase(),
        date: new Date(file.created_at).toISOString().split('T')[0]
      })));
      
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to upload files. Please try again.');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleCrawlSubmit = async () => {
    if (!crawlUrlInput.trim() || !isValidUrl) {
      setError('Please enter a valid HTTPS URL');
      return;
    }

    setCrawling(true);
    setError(null);
    setCrawlStatus(null);

    const crawlData = {
      url: crawlUrlInput.trim(),
      max_page: maxPage,
      max_depth: maxDepth,
      crawl_sub_domain: crawlSubDomain
    };

    try {
      if (!apiKey) {
        throw new Error('API key is missing');
      }

      const response = await crawlWebsite(crawlData, apiKey, endpoint);
      const uploadId = response.data?.upload_id || response.upload_id;
      
      if (uploadId) {
        setCrawlStatus({
          uploadId,
          status: 'started',
          message: 'Crawling started...'
        });

        const interval = setInterval(async () => {
          try {
            const statusResponse = await getUploadStatus(uploadId, apiKey, endpoint);
            const status = statusResponse.data || statusResponse;
            
            setCrawlStatus({
              uploadId,
              status: status.status || 'processing',
              message: status.message || 'Processing...',
              progress: status.progress || 0,
              pages_crawled: status.pages_crawled || 0
            });

            if (status.status === 'completed' || status.status === 'failed') {
              clearInterval(interval);
              setStatusInterval(null);
              setCrawling(false);
              
              if (status.status === 'completed') {
                setSuccess(true);
                fetchDocuments();
                setTimeout(() => {
                  setCrawlDialogOpen(false);
                  setCrawlStatus(null);
                }, 2000);
              } else {
                setError(status.message || 'Crawling failed');
              }
            }
          } catch (err) {
            console.error('Error checking crawl status:', err);
          }
        }, 2000);

        setStatusInterval(interval);
      }

      setCrawlDialogOpen(false);
    } catch (err) {
      setError(err.message || 'Failed to start web crawling. Please try again.');
      setCrawling(false);
    }
  };

  const handleDelete = async (docId) => {
    try {
      if (!apiKey) {
        throw new Error('API key is missing');
      }
      
      await deleteFile(docId, apiKey, endpoint);
      setDocuments(prev => prev.filter(doc => doc.id !== docId));
    } catch (err) {
      setError(err.message || 'Failed to delete document');
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      pdf: 'error',
      txt: 'success',
      docx: 'primary',
      xlsx: 'primary',
      pptx: 'primary',
      md: 'warning',
      adoc: 'warning',
      asciidoc: 'warning',
      html: 'info',
      xhtml: 'info',
      htm: 'info',
      csv: 'success',
      zip: 'secondary',
      png: 'info',
      jpg: 'info',
      jpeg: 'info',
      tiff: 'info',
      tif: 'info',
      bmp: 'info',
      webp: 'info'
    };
    return colors[type] || 'default';
  };

  const handleCloseCrawlDialog = () => {
    setCrawlDialogOpen(false);
    setCrawlUrlInput('');
    setMaxPage(100);
    setMaxDepth(3);
    setCrawlSubDomain(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>RAG Document Management</Typography>
      
      <Paper sx={{ p: 3, mt: 2 }}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 2 }}>
            <input
              accept=".pdf,.txt,.doc,.docx,.xlsx,.pptx,.md,.zip,.adoc,.asciidoc,.html,.xhtml,.htm,.csv,.png,.jpg,.jpeg,.tiff,.tif,.bmp,.webp"
              style={{ display: 'none' }}
              id="raised-button-file"
              type="file"
              onChange={handleFileUpload}
              multiple
            />
            <label htmlFor="raised-button-file">
              <Button
                variant="contained"
                component="span"
                startIcon={<CloudUploadIcon />}
                disabled={uploading || !apiKey}
                size="large"
              >
                Upload Documents
              </Button>
            </label>

            <Button
              variant="outlined"
              startIcon={<LanguageIcon />}
              onClick={() => setCrawlDialogOpen(true)}
              disabled={crawling || !apiKey}
              size="large"
              sx={{
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.dark',
                  backgroundColor: 'rgba(64, 196, 99, 0.1)'
                }
              }}
            >
              Crawl Website
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Upload documents (PDF, DOCX, XLSX, PPTX, MD, HTML, CSV, Images) or crawl websites for RAG processing
          </Typography>
        </Box>

        {crawlStatus && (
          <Alert 
            severity={crawlStatus.status === 'failed' ? 'error' : 'info'} 
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {crawlStatus.message}
              </Typography>
              {crawlStatus.pages_crawled > 0 && (
                <Typography variant="body2">
                  Pages crawled: {crawlStatus.pages_crawled}
                </Typography>
              )}
              {crawlStatus.progress > 0 && (
                <Typography variant="body2">
                  Progress: {crawlStatus.progress}%
                </Typography>
              )}
            </Box>
          </Alert>
        )}

        {uploading && (
          <Box sx={{ textAlign: 'center', my: 2 }}>
            <CircularProgress size={24} />
            <Typography sx={{ mt: 1 }}>Uploading...</Typography>
          </Box>
        )}

        {crawling && (
          <Box sx={{ textAlign: 'center', my: 2 }}>
            <CircularProgress size={24} />
            <Typography sx={{ mt: 1 }}>Crawling website...</Typography>
          </Box>
        )}

        {loading && (
          <Box sx={{ textAlign: 'center', my: 2 }}>
            <CircularProgress size={24} />
            <Typography sx={{ mt: 1 }}>Loading documents...</Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Operation completed successfully!
          </Alert>
        )}

        <List>
          {documents.map((doc) => (
            <ListItem
              key={doc.id}
              secondaryAction={
                <Box>
                  <Tooltip title="Preview">
                    <IconButton 
                      edge="end" 
                      onClick={() => {
                        setSelectedDoc(doc);
                        setPreviewOpen(true);
                      }}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton 
                      edge="end" 
                      onClick={() => handleDelete(doc.id)}
                      sx={{ ml: 1 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
              sx={{ 
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
              <ListItemText
                primary={doc.name}
                secondary={
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip 
                      label={doc.type.toUpperCase()} 
                      size="small" 
                      color={getTypeColor(doc.type)}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {doc.size} • {doc.date}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>

        {documents.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              No documents uploaded yet
            </Typography>
          </Box>
        )}
      </Paper>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Document Preview: {selectedDoc?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.secondary">
              Preview not available for this file type
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={crawlDialogOpen} 
        onClose={handleCloseCrawlDialog}
        PaperProps={{
          sx: {
            borderRadius: 2,
            width: '500px',
            maxWidth: '90vw'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Typography variant="h6">Crawl Website</Typography>
          <IconButton onClick={handleCloseCrawlDialog} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the URL of a website to crawl and index its content
          </Typography>
          
          <TextField
            label="Website URL"
            placeholder="https://example.com"
            value={crawlUrlInput}
            onChange={e => setCrawlUrlInput(e.target.value)}
            fullWidth
            sx={{ mb: 1 }}
            disabled={crawling}
            error={crawlUrlInput.trim() !== '' && !isValidUrl}
            helperText={crawlUrlInput.trim() !== '' && !isValidUrl ? "URL must start with https://" : ""}
          />
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Max Pages (Default: 100)
            </Typography>
            <Slider
              value={maxPage}
              onChange={(e, newValue) => setMaxPage(newValue)}
              min={10}
              max={500}
              step={10}
              valueLabelDisplay="auto"
              disabled={crawling}
            />
          </Box>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Max Depth (Default: 3)
            </Typography>
            <Slider
              value={maxDepth}
              onChange={(e, newValue) => setMaxDepth(newValue)}
              min={1}
              max={10}
              step={1}
              valueLabelDisplay="auto"
              disabled={crawling}
            />
          </Box>
          
          <FormControlLabel
            control={
              <Checkbox 
                checked={crawlSubDomain}
                onChange={(e) => setCrawlSubDomain(e.target.checked)}
                disabled={crawling}
              />
            }
            label="Crawl Subdomains (Default: Yes)"
          />
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            onClick={handleCloseCrawlDialog} 
            variant="outlined"
            sx={{ borderRadius: 1.5 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCrawlSubmit} 
            variant="contained"
            disabled={crawling || !crawlUrlInput.trim() || !isValidUrl}
            sx={{ borderRadius: 1.5 }}
          >
            {crawling ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Crawling...
              </>
            ) : (
              'Start Crawling'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RAGUpload;
